import React from "react";
import { shallowEqual } from "react-redux";
import { CircleMarker, Polyline, Popup, useMap } from "react-leaflet";
import { Map } from "leaflet";
import polyline from "@mapbox/polyline";
import * as turf from "@turf/turf";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "../../redux/hooks";
import variableColors from "../../_App.module.scss";
import { getSpeedColor } from "../../utils/swiftlyHelpers";
import { PopupContainer } from "../views/SelectedBusViewMap";
import { stopsOptionsConstant } from "../../data/constants";
import { LatLngBoundsExpression } from "leaflet";
import { zoomRange } from "../../config";
import AverageSpeedsMapLegend from "./AverageSpeedsMapLegend";

const { darkColor } = variableColors;

const AverageSpeedsMap = (): JSX.Element => {
  const { t, ready } = useTranslation();
  const leafletMap: Map = useMap();

  const availableSpeeds: string[] = React.useMemo(() => {
    return [
      t("Prędkość średnia"),
      `${t("Mediana")} P50`,
      t("Prędkość min"),
      t("Prędkość max"),
      `${t("Prędkość")} P10`,
      `${t("Prędkość")} P25`,
      `${t("Prędkość")} P75`,
      `${t("Prędkość")} P90`,
    ];
  }, [t]);

  const [
    routesDetails,
    averageSpeedData,
    encodedPolyline,
    stopsOrderData,
    allStopsArrayFiltered,
    averageSpeedsRouteId,
    averageSpeedsDirectionId,
  ]: [RouteDetails[], AverageSpeedData[], string, string[], StopInfo[], string, string] = useAppSelector(
    (state: RootState) => [
      state?.routesInfo?.routesInfo,
      state?.swiftly?.averageSpeedData,
      state?.swiftly?.encodedPolyline,
      state?.swiftly?.stopsOrderData,
      state?.routesInfo?.allStopsArrayFiltered,
      state?.swiftly?.averageSpeedsRouteId,
      state?.swiftly?.averageSpeedsDirectionId,
    ],
    shallowEqual
  );

  const [speeds, setSpeeds] = React.useState<number[] | null>(null);
  const [polylineSegments, setPolylineSegments] = React.useState<[number, number][][] | null>(null);
  const [stops, setStops] = React.useState<StopInfo[] | null>(null);
  const [kindOfSpeed, setKindOfSpeed] = React.useState<string>("");
  // console.log("kindOfSpeed:", kindOfSpeed);
  // console.log("speeds:", speeds);

  const baseUnit: string = [
    ...new Set(
      averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.filter((elem: AverageSpeedObj | undefined) => elem !== undefined)
        ?.map((elem: AverageSpeedObj | undefined) => elem?.units)
    ),
  ]?.[0] as string;
  // console.log("baseUnit:", baseUnit);
  const baseLengthUnit = baseUnit === "kmh" ? "kilometers" : baseUnit === "mph" ? "miles" : "kilometers";
  // console.log("baseLengthUnit:", baseLengthUnit);

  const currentRouteData = routesDetails?.find((route: RouteDetails) => route?.id === averageSpeedsRouteId) as RouteDetails;
  // console.log("currentRouteData:", currentRouteData);

  //* Set initial Average Speed to kindOfSpeed state
  React.useEffect(() => {
    if (ready) {
      setKindOfSpeed(availableSpeeds[0]?.replace(" ", "_"));
    }
  }, [availableSpeeds, ready]);

  //* Fit map bounds
  React.useEffect(() => {
    if (!leafletMap) return;
    if (leafletMap && polylineSegments) {
      const polylinePoints: [number, number][] = polylineSegments?.flat(1);
      const lanArray: number[] = polylinePoints?.map((point: [number, number]) => point?.[0]);
      const lonArray: number[] = polylinePoints?.map((point: [number, number]) => point?.[1]);

      const [minLan, maxLan]: [number, number] = [Math.min(...lanArray), Math.max(...lanArray)];
      const [minLon, maxLon]: [number, number] = [Math.min(...lonArray), Math.max(...lonArray)];

      const bounds: [number, number][] = [
        [minLan - zoomRange, minLon - zoomRange],
        [maxLan + zoomRange, maxLon + zoomRange],
      ];
      // console.log("bounds:", bounds);
      leafletMap.fitBounds(bounds as LatLngBoundsExpression);
    }
  }, [leafletMap, polylineSegments]);

  //* Set averageSpeeds
  React.useEffect(() => {
    if (averageSpeedData) {
      const averageSpeeds: number[] = averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.map((elem: AverageSpeedObj | undefined) => Number(elem?.avg_speed));
      // console.log("averageSpeeds:", averageSpeeds);

      const medianSpeeds: number[] = averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.map((elem: AverageSpeedObj | undefined) => Number(elem?.median_speed));
      // console.log("medianSpeeds:", medianSpeeds);

      const minSpeeds: number[] = averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.map((elem: AverageSpeedObj | undefined) => Number(elem?.min_speed));
      // console.log("minSpeeds:", minSpeeds);

      const maxSpeeds: number[] = averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.map((elem: AverageSpeedObj | undefined) => Number(elem?.max_speed));
      // console.log("maxSpeeds:", maxSpeeds);

      const p10Speeds: number[] = averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.map((elem: AverageSpeedObj | undefined) => Number(elem?.p10_speed));
      // console.log("p10Speeds:", p10Speeds);

      const p25Speeds: number[] = averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.map((elem: AverageSpeedObj | undefined) => Number(elem?.p25_speed));
      // console.log("p25Speeds:", p25Speeds);

      const p75Speeds: number[] = averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.map((elem: AverageSpeedObj | undefined) => Number(elem?.p75_speed));
      // console.log("p75Speeds:", p75Speeds);

      const p90Speeds: number[] = averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.map((elem: AverageSpeedObj | undefined) => Number(elem?.p90_speed));
      // console.log("p90Speeds:", p90Speeds);

      const speedToShow: number[] =
        kindOfSpeed === availableSpeeds[0].replace(" ", "_")
          ? averageSpeeds
          : kindOfSpeed === availableSpeeds[1].replace(" ", "_")
          ? medianSpeeds
          : kindOfSpeed === availableSpeeds[2].replace(" ", "_")
          ? minSpeeds
          : kindOfSpeed === availableSpeeds[3].replace(" ", "_")
          ? maxSpeeds
          : kindOfSpeed === availableSpeeds[4].replace(" ", "_")
          ? p10Speeds
          : kindOfSpeed === availableSpeeds[5].replace(" ", "_")
          ? p25Speeds
          : kindOfSpeed === availableSpeeds[6].replace(" ", "_")
          ? p75Speeds
          : p90Speeds;
      // console.log("speedToShow:", speedToShow);

      setSpeeds(null);
      setTimeout(() => {
        setSpeeds(speedToShow);
      }, 0);
    } else setSpeeds(null);

    return () => setSpeeds(null);
  }, [availableSpeeds, averageSpeedData, kindOfSpeed]);

  //* Set polyline segments + stops
  React.useEffect(() => {
    if (allStopsArrayFiltered && encodedPolyline) {
      try {
        const stopsViaRouteForTurf: [number, number][] = stopsOrderData?.map((stopId: string) => {
          const currentStop: StopInfo = allStopsArrayFiltered?.find((stop: StopInfo) => stop?.stopId === stopId) as StopInfo;
          return [currentStop?.lon, currentStop?.lat] as [number, number];
        });

        //* Remove first and last stops
        const stopsViaRouteForTurfModified: [number, number][] = stopsViaRouteForTurf?.slice(1, -1);
        // console.log("stopsViaRouteForTurfModified:", stopsViaRouteForTurfModified);

        const decodedPolylineForTurf: number[][] = polyline
          .decode(encodedPolyline)
          ?.map((point: [number, number]) => [point?.[1], point[0]]);
        // console.log("decodedPolylineForTurf:", decodedPolylineForTurf);

        const routeLine: turf.helpers.Feature<turf.helpers.LineString, turf.helpers.Properties> =
          turf.lineString(decodedPolylineForTurf);
        // console.log("routeLine:", routeLine);
        const routeLength: number = turf.length(routeLine, { units: baseLengthUnit });
        // console.log("routeLength:", routeLength);

        // Get distances along the route for each stop (in kilometers)
        const stopDistances: number[] = stopsViaRouteForTurfModified
          ?.map((stop: [number, number]) => {
            return turf.nearestPointOnLine(routeLine, stop, { units: baseLengthUnit }).properties?.location as number;
          })
          ?.sort((a: number, b: number) => a - b);
        // console.log("stopDistances:", stopDistances);
        const stopDistancesWithBeginningAndEnd: number[] = [...new Set([0, ...stopDistances, routeLength])];
        // console.log("stopDistancesWithBeginningAndEnd:", stopDistancesWithBeginningAndEnd);

        // Build segments by slicing the routeLine between stop distances
        const segments = [] as turf.helpers.Feature<turf.helpers.LineString, turf.helpers.Properties>[];
        for (let i = 0; i < stopDistancesWithBeginningAndEnd?.length - 1; i++) {
          const segmentLine = turf.lineSliceAlong(
            routeLine,
            stopDistancesWithBeginningAndEnd[i],
            stopDistancesWithBeginningAndEnd[i + 1],
            {
              units: baseLengthUnit,
            }
          );
          segments.push(segmentLine);
        }
        // console.log("segments:", segments);

        // Convert GeoJSON segments back to LatLng arrays
        const segmentsLatLng = segments?.map(
          (segment: turf.helpers.Feature<turf.helpers.LineString, turf.helpers.Properties>) =>
            segment.geometry.coordinates.map(([lng, lat]) => [lat, lng])
        ) as [number, number][][];
        // console.log("segmentsLatLng:", segmentsLatLng);
        setPolylineSegments(segmentsLatLng);

        const stopsWithSequence: StopInfo[] = stopsOrderData?.map((stopId: string) => {
          const currentStop: StopInfo = allStopsArrayFiltered?.find((stop: StopInfo) => stop?.stopId === stopId) as StopInfo;
          return { ...currentStop };
        });
        setStops(stopsWithSequence);
      } catch (error) {
        console.log("error:", error);
      }

      return () => {
        setPolylineSegments(null);
        setStops(null);
      };
    }
  }, [allStopsArrayFiltered, baseLengthUnit, encodedPolyline, stopsOrderData]);

  //* Polyline
  const polylinesFromSegments: JSX.Element[] | undefined = polylineSegments?.map(
    (segment: [number, number][], index: number): JSX.Element => {
      const segmentsIds = averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.map((elem: AverageSpeedObj | undefined) => elem?.segment_id) as string[];

      const unit = baseUnit === "kmh" ? "km/h" : baseUnit === "mph" ? "mph" : "";

      const averageSpeeds: number[] = averageSpeedData
        ?.map((elem: AverageSpeedData) => Object.values(elem)?.[0])
        ?.map((elem: AverageSpeedObj | undefined) => Number(elem?.avg_speed));
      // console.log("averageSpeeds:", averageSpeeds);

      return (
        <Polyline
          key={`${index}_${segment?.toString()}`}
          pathOptions={{ weight: 6 }}
          color={getSpeedColor(speeds?.[index] as number, baseUnit) || darkColor}
          positions={segment}
          children={
            <Popup>
              <PopupContainer>
                <div className="popup-line">
                  {`${t("Id segmentu")}:`} <span className="span_maroon">{segmentsIds?.[index] || t("b/d")}</span>
                </div>
                {t("Prędkość średnia").replace(" ", "_") !== kindOfSpeed ? (
                  <div className="popup-line">
                    {`${t("Prędkość średnia")}:`}{" "}
                    <span className="span_maroon span_italic">
                      {averageSpeeds?.[index] ? `${averageSpeeds?.[index]} ${unit}` : t("b/d")}
                    </span>
                  </div>
                ) : null}
                <div className="popup-line">
                  {`${kindOfSpeed.replace("_", " ")}:`}{" "}
                  <span className="span_maroon">{speeds?.[index] ? `${speeds?.[index]} ${unit}` : t("b/d")}</span>
                </div>
              </PopupContainer>
            </Popup>
          }
        />
      );
    }
  );

  //* Stops
  const polylineStops: JSX.Element[] | undefined = stops?.map((stop: StopInfo, index: number): JSX.Element => {
    return (
      <CircleMarker
        key={`${index}_${stop?.stopId}`}
        center={[stop?.lat as number, stop?.lon as number]}
        pathOptions={{
          ...stopsOptionsConstant,
          fillOpacity: 0.4,
        }}
        radius={4}
      >
        <Popup>
          <PopupContainer>
            <div className="popup-line">
              {t("Nazwa")} <span className="span_maroon">{stop?.stopName}</span>
            </div>
            <div className="popup-line">
              {t("Id")} <span className="span_maroon">{stop?.stopId}</span>
            </div>
            <div className="popup-line text-muted small">
              {t("Pozycja")}: <span className="span_bold text-muted">{`${stop?.lat} ${stop?.lon}`}</span>
            </div>
            <div className="popup-line text-muted small">
              {t("Nr")}: <span className="span_bold text-muted">{`${index + 1}/ ${stops?.length}`}</span>
            </div>
          </PopupContainer>
        </Popup>
      </CircleMarker>
    );
  });

  return (
    <React.Fragment>
      {averageSpeedData && encodedPolyline ? (
        <React.Fragment>
          {polylineSegments && speeds ? polylinesFromSegments : null}
          {stops ? polylineStops : null}
        </React.Fragment>
      ) : null}

      {currentRouteData && averageSpeedsDirectionId && baseUnit ? (
        <AverageSpeedsMapLegend
          currentRouteData={currentRouteData}
          averageSpeedsDirectionId={averageSpeedsDirectionId}
          baseUnit={baseUnit}
          kindOfSpeed={kindOfSpeed}
          setKindOfSpeed={setKindOfSpeed}
          availableSpeeds={availableSpeeds}
        />
      ) : null}
    </React.Fragment>
  );
};

export default AverageSpeedsMap;
